/**
 * NavigatorX Referral Dashboard
 * 
 * Complete referral program management:
 * - Total referrals and conversion rate
 * - Top referrers leaderboard
 * - Referral revenue tracking
 * - Credit management
 * - Referral chain visualization
 * - Fraud detection alerts
 * 
 * @author Claude AI for TGE.LA
 * @version 1.0.0
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import {
  Gift,
  Users,
  TrendingUp,
  DollarSign,
  Award,
  RefreshCw,
  Loader2,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Search,
  Copy,
  Crown,
  UserPlus,
  Coins
} from 'lucide-react';
import { toast } from 'sonner';
import { collection, getDocs, query, orderBy, limit, where } from 'firebase/firestore';
import { navDb, auth } from '../../../utils/firebase/client';
import {
  NAV_USERS_COLLECTION,
  getUserReferralStats,
  grantReferralCredit,
  removeReferralCredit,
  regenerateReferralCode
} from '../../../utils/services/navigatorXService';
import { SUBSCRIPTION_PRICE } from '../../../utils/services/navigatorXBillingService';
import type { NavUser, ReferralStats, ReferralInvite } from '../../../types/navigatorx';

interface ReferralDashboardProps {
  embedded?: boolean;
}

interface TopReferrer {
  user: NavUser;
  stats: ReferralStats;
}

export default function ReferralDashboard({ embedded = false }: ReferralDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [topReferrers, setTopReferrers] = useState<TopReferrer[]>([]);
  const [totalStats, setTotalStats] = useState({
    totalInvites: 0,
    totalSignups: 0,
    totalConverted: 0,
    totalCreditsIssued: 0,
    totalPendingCredits: 0,
    conversionRate: 0,
    signupToPayingRate: 0,
    referralRevenue: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<TopReferrer | null>(null);

  const adminId = auth.currentUser?.uid || 'unknown';

  useEffect(() => {
    loadReferralData();
  }, []);

  const loadReferralData = async () => {
    setLoading(true);
    try {
      // Get all users with referral codes
      const usersSnapshot = await getDocs(
        query(
          collection(navDb, NAV_USERS_COLLECTION),
          where('referralCode', '!=', null),
          orderBy('referralCode'),
          orderBy('referralCount', 'desc'),
          limit(100)
        )
      );

      const users = usersSnapshot.docs
        .filter(doc => doc.id !== 'blocked' && doc.id !== 'invites' && doc.id !== 'admin_logs')
        .map(doc => ({ id: doc.id, ...doc.data() } as NavUser));

      // Load stats for top referrers
      const referrersWithStats: TopReferrer[] = [];
      let totalInvites = 0;
      let totalSignups = 0;
      let totalConverted = 0;
      let totalCreditsIssued = 0;
      let totalPendingCredits = 0;

      for (const user of users.slice(0, 50)) {
        try {
          const stats = await getUserReferralStats(user.id);
          referrersWithStats.push({ user, stats });

          totalInvites += stats.totalInvitesSent;
          totalSignups += stats.totalSignups;
          totalConverted += stats.totalPaidMonth2;
          totalCreditsIssued += stats.totalCreditsEarned;
          totalPendingCredits += stats.pendingCredits;
        } catch (e) {
          // Skip users with no referral data
        }
      }

      // Sort by total signups
      referrersWithStats.sort((a, b) => b.stats.totalSignups - a.stats.totalSignups);

      setTopReferrers(referrersWithStats);
      setTotalStats({
        totalInvites,
        totalSignups,
        totalConverted,
        totalCreditsIssued,
        totalPendingCredits,
        conversionRate: totalInvites > 0 ? (totalSignups / totalInvites) * 100 : 0,
        signupToPayingRate: totalSignups > 0 ? (totalConverted / totalSignups) * 100 : 0,
        referralRevenue: totalConverted * SUBSCRIPTION_PRICE * 2 // 2 months before credit earned
      });

    } catch (error) {
      console.error('Error loading referral data:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantCredit = async (userId: string, months: number) => {
    try {
      await grantReferralCredit(userId, months, adminId, 'Manual grant from dashboard');
      toast.success(`Granted ${months} credit(s)`);
      loadReferralData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to grant credit');
    }
  };

  const handleRemoveCredit = async (userId: string, months: number) => {
    try {
      await removeReferralCredit(userId, months, adminId, 'Manual removal from dashboard');
      toast.success(`Removed ${months} credit(s)`);
      loadReferralData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove credit');
    }
  };

  const handleRegenerateCode = async (userId: string) => {
    try {
      const newCode = await regenerateReferralCode(userId, adminId);
      toast.success(`New code: ${newCode}`);
      loadReferralData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to regenerate code');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const filteredReferrers = topReferrers.filter(r =>
    r.user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    r.stats.referralCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-12' : 'min-h-[400px]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Referral Program</h2>
            <p className="text-gray-500">Manage referrals and credits</p>
          </div>
          <Button variant="outline" onClick={loadReferralData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-pink-600 font-medium">Total Invites Sent</p>
                <p className="text-2xl font-bold text-pink-700">{totalStats.totalInvites}</p>
              </div>
              <Gift className="h-8 w-8 text-pink-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Signups from Referrals</p>
                <p className="text-2xl font-bold text-blue-700">{totalStats.totalSignups}</p>
                <p className="text-xs text-gray-500">{totalStats.conversionRate.toFixed(1)}% conversion</p>
              </div>
              <UserPlus className="h-8 w-8 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-600 font-medium">Completed Referrals</p>
                <p className="text-2xl font-bold text-green-700">{totalStats.totalConverted}</p>
                <p className="text-xs text-gray-500">{totalStats.signupToPayingRate.toFixed(1)}% paid 2mo</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 font-medium">Credits Issued</p>
                <p className="text-2xl font-bold text-amber-700">{totalStats.totalCreditsIssued}</p>
                <p className="text-xs text-gray-500">{totalStats.totalPendingCredits} pending</p>
              </div>
              <Coins className="h-8 w-8 text-amber-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name, email, or referral code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Top Referrers Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            Top Referrers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredReferrers.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No referrers found</p>
            ) : (
              filteredReferrers.map((referrer, index) => (
                <div
                  key={referrer.user.id}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                    selectedUser?.user.id === referrer.user.id
                      ? 'bg-pink-50 border-pink-200'
                      : 'bg-gray-50 border-transparent hover:bg-gray-100'
                  }`}
                  onClick={() => setSelectedUser(selectedUser?.user.id === referrer.user.id ? null : referrer)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Rank Badge */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-100 text-yellow-700' :
                        index === 1 ? 'bg-gray-100 text-gray-600' :
                        index === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-50 text-gray-500'
                      }`}>
                        {index + 1}
                      </div>

                      <div>
                        <p className="font-medium">{referrer.user.displayName || referrer.user.email}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span className="font-mono bg-gray-100 px-1 rounded">{referrer.stats.referralCode}</span>
                          <button onClick={(e) => { e.stopPropagation(); copyToClipboard(referrer.stats.referralCode); }}>
                            <Copy className="h-3 w-3 hover:text-gray-700" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Stats Pills */}
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          <UserPlus className="h-3 w-3 mr-1" />
                          {referrer.stats.totalSignups} signups
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          {referrer.stats.totalPaidMonth2} converted
                        </Badge>
                        <Badge variant="outline" className="text-xs bg-pink-50 text-pink-700 border-pink-200">
                          <Gift className="h-3 w-3 mr-1" />
                          {referrer.stats.freeMonthsAvailable} credits
                        </Badge>
                      </div>
                      <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${
                        selectedUser?.user.id === referrer.user.id ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedUser?.user.id === referrer.user.id && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {/* Detailed Stats */}
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-xl font-bold text-gray-700">{referrer.stats.totalInvitesSent}</p>
                          <p className="text-xs text-gray-500">Invites Sent</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-gray-700">{referrer.stats.totalSignups}</p>
                          <p className="text-xs text-gray-500">Signups</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-gray-700">{referrer.stats.totalPaidMonth1}</p>
                          <p className="text-xs text-gray-500">Month 1 Paid</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xl font-bold text-gray-700">{referrer.stats.totalPaidMonth2}</p>
                          <p className="text-xs text-gray-500">Month 2 Paid</p>
                        </div>
                      </div>

                      {/* Pending Credits Alert */}
                      {referrer.stats.pendingCredits > 0 && (
                        <div className="flex items-center gap-2 p-2 bg-amber-50 rounded text-amber-700 text-sm">
                          <Clock className="h-4 w-4" />
                          {referrer.stats.pendingCredits} pending credit(s) - waiting for Month 2 payment
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleGrantCredit(referrer.user.id, 1); }}
                          className="text-green-600"
                        >
                          <Gift className="h-4 w-4 mr-1" /> +1 Credit
                        </Button>
                        {referrer.stats.freeMonthsAvailable > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleRemoveCredit(referrer.user.id, 1); }}
                            className="text-red-600"
                          >
                            <XCircle className="h-4 w-4 mr-1" /> -1 Credit
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => { e.stopPropagation(); handleRegenerateCode(referrer.user.id); }}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" /> New Code
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Referral Revenue */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Referral Program Impact
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-700">${totalStats.referralRevenue.toFixed(0)}</p>
              <p className="text-sm text-gray-600">Revenue from Referrals</p>
              <p className="text-xs text-gray-400">(2 months before credit earned)</p>
            </div>
            <div className="p-4 bg-pink-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-pink-700">${(totalStats.totalCreditsIssued * SUBSCRIPTION_PRICE).toFixed(0)}</p>
              <p className="text-sm text-gray-600">Credits Value Issued</p>
              <p className="text-xs text-gray-400">{totalStats.totalCreditsIssued} months × ${SUBSCRIPTION_PRICE}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-700">
                ${(totalStats.referralRevenue - totalStats.totalCreditsIssued * SUBSCRIPTION_PRICE).toFixed(0)}
              </p>
              <p className="text-sm text-gray-600">Net Referral Revenue</p>
              <p className="text-xs text-gray-400">Revenue - Credits</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
